"use strict";
/**
 * Ediabas Configuration Schema
 *
 * Zod schemas for validating configuration files
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CONFIG = exports.EdiabasConfigFileSchema = exports.LoggingConfigSchema = exports.TimeoutsConfigSchema = exports.PathsConfigSchema = exports.InterfaceConfigSchema = exports.GatewayConfigSchema = exports.SimulationConfigSchema = exports.IcomConfigSchema = exports.EnetConfigSchema = exports.SerialConfigSchema = exports.InterfaceTypeSchema = void 0;
const zod_1 = require("zod");
/**
 * Interface type enum
 */
exports.InterfaceTypeSchema = zod_1.z.enum([
    'simulation',
    'serial',
    'enet',
    'gateway',
    'icom',
]);
/**
 * Serial interface configuration
 */
exports.SerialConfigSchema = zod_1.z.object({
    port: zod_1.z.string().describe('Serial port path (e.g., /dev/ttyUSB0, COM3)'),
    baudRate: zod_1.z.number().int().positive().default(9600),
    dataBits: zod_1.z.union([zod_1.z.literal(7), zod_1.z.literal(8)]).default(8),
    parity: zod_1.z.enum(['none', 'even', 'odd']).default('none'),
    stopBits: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2)]).default(1),
    protocol: zod_1.z.enum(['kline', 'dcan', 'isotp', 'tp20']).optional(),
    testerAddress: zod_1.z.number().int().min(0).max(255).optional(),
    ecuAddress: zod_1.z.number().int().min(0).max(255).optional(),
    testerCanId: zod_1.z.number().int().min(0).optional(),
    ecuCanId: zod_1.z.number().int().min(0).optional(),
});
/**
 * ENET (Ethernet) interface configuration
 */
exports.EnetConfigSchema = zod_1.z.object({
    host: zod_1.z.string().describe('ECU IP address or hostname'),
    port: zod_1.z.number().int().min(1).max(65535).default(6801),
    protocol: zod_1.z.enum(['hsfz', 'doip']).default('hsfz'),
    testerAddress: zod_1.z.number().int().min(0).optional(),
    ecuAddress: zod_1.z.number().int().min(0).optional(),
});
/**
 * ICOM interface configuration
 */
exports.IcomConfigSchema = zod_1.z.object({
    host: zod_1.z.string().describe('ICOM device IP address'),
    port: zod_1.z.number().int().min(1).max(65535).default(6801),
    vehicleIdent: zod_1.z.string().optional(),
});
/**
 * Simulation interface configuration
 */
exports.SimulationConfigSchema = zod_1.z.object({
    responsesFile: zod_1.z.string().optional().describe('Path to response simulation file'),
    latencyMs: zod_1.z.number().int().min(0).default(0),
});
/**
 * Gateway interface configuration (remote JSON-RPC connection)
 */
exports.GatewayConfigSchema = zod_1.z.object({
    host: zod_1.z.string().default('127.0.0.1').describe('Gateway server IP or hostname'),
    port: zod_1.z.number().int().min(1).max(65535).default(6801).describe('Gateway server port'),
});
/**
 * Interface configuration (discriminated union)
 */
exports.InterfaceConfigSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal('simulation'), simulation: exports.SimulationConfigSchema.optional() }),
    zod_1.z.object({ type: zod_1.z.literal('serial'), serial: exports.SerialConfigSchema }),
    zod_1.z.object({ type: zod_1.z.literal('enet'), enet: exports.EnetConfigSchema }),
    zod_1.z.object({ type: zod_1.z.literal('gateway'), gateway: exports.GatewayConfigSchema.optional() }),
    zod_1.z.object({ type: zod_1.z.literal('icom'), icom: exports.IcomConfigSchema }),
]);
/**
 * Path configuration
 */
exports.PathsConfigSchema = zod_1.z.object({
    sgbd: zod_1.z.string().describe('Path to SGBD/ECU files directory'),
    trace: zod_1.z.string().optional().describe('Path for trace/log files'),
    grp: zod_1.z.string().optional().describe('Path to GRP group files'),
});
/**
 * Timeout configuration
 */
exports.TimeoutsConfigSchema = zod_1.z.object({
    connect: zod_1.z.number().int().positive().default(5000).describe('Connection timeout in ms'),
    response: zod_1.z.number().int().positive().default(2000).describe('Response timeout in ms'),
    idle: zod_1.z.number().int().positive().optional().describe('Idle disconnect timeout in ms'),
});
/**
 * Logging configuration — mirrors `@emdzej/bimmerz-logger`'s
 * `LoggerConfig` shape so the CLI can hand the file's `logging`
 * section straight to `configureLogger()` (with env-var overrides
 * layered on top via `EDIABASX_LOG_*`).
 *
 * Notable change vs. 0.2.x:
 *
 * - **`enabled` is gone.** The per-instance `Ediabas.config.logging`
 *   boolean it fed has been deleted; wire-level + lifecycle tracing
 *   now routes through the standard logger. To get the same effect,
 *   set `level: 'debug'` (lifecycle metadata) or
 *   `categories: { 'EDIABASX.ediabas.wire': 'trace' }` (raw bytes).
 * - **`file` → `destination`, `console` / `timestamps` / `hexDump` gone.**
 *   The sink (pretty stdout vs. JSON vs. file) is decided by the
 *   logger; the file format is whatever pino-pretty (`pretty: true`)
 *   or pino's JSON renders.
 */
exports.LoggingConfigSchema = zod_1.z.object({
    /** Default level when no `categories` entry matches. */
    level: zod_1.z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent']).default('info'),
    /**
     * Per-category levels. Dot-separated keys; lookup walks up the
     * path, so a rule for `EDIABASX` applies to `EDIABASX.ediabas`,
     * `EDIABASX.ediabas.wire`, etc. unless a more specific rule wins.
     */
    categories: zod_1.z.record(zod_1.z.string(), zod_1.z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent'])).optional(),
    /** File path for log output. When set, logs go to file instead of stdout. */
    destination: zod_1.z.string().optional().describe('Log file path'),
    /** Render through pino-pretty (TTY-friendly). Ignored when destination is set. */
    pretty: zod_1.z.boolean().optional().describe('Use pino-pretty colour render'),
});
/**
 * Main Ediabas configuration schema
 */
exports.EdiabasConfigFileSchema = zod_1.z.object({
    /** Configuration file version */
    version: zod_1.z.literal(1).default(1),
    /** Interface configuration */
    interface: exports.InterfaceConfigSchema,
    /** Paths configuration */
    paths: exports.PathsConfigSchema,
    /** Timeouts configuration */
    timeouts: exports.TimeoutsConfigSchema.optional(),
    /** Logging configuration */
    logging: exports.LoggingConfigSchema.optional(),
});
/**
 * Default configuration values
 */
exports.DEFAULT_CONFIG = {
    version: 1,
    timeouts: {
        connect: 5000,
        response: 2000,
    },
    logging: {
        level: 'info',
    },
};
//# sourceMappingURL=config-schema.js.map